import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { Box, ButtonLink, Heading, Stack, Text } from '@eneco/sparky';

import type { CtaBlockButton, CtaBlockContent } from './cta-block-content.types';

/**
 * `ctaBlock` block for the CMS-agnostic lane. POC-schema-only (see `cta-block-content.types.ts`).
 * Buttons follow the exact same `adapter.toLink` pattern as Hero's `PocHeroText` — see that component
 * for why the button shape needs the (now-fixed) `LINK_REFERENCE_PROJECTION` in `queries.ts` to
 * resolve at all.
 */
const CtaBlock: FC = () => {
  const { adapter } = useCms();
  const content = useContent<CtaBlockContent>();
  const buttons = content.buttons ?? [];

  if (!content.title && !content.description && buttons.length === 0) return null;

  return (
    <Box backgroundColor="backgroundBrand" padding="10" borderRadius="m">
      <Stack gap="6" alignX="center" alignY="center">
        {content.title && <Heading as="h2">{content.title}</Heading>}
        {content.description && <Text>{content.description}</Text>}
        {buttons.length > 0 && (
          <Stack direction="row" gap="4">
            {buttons.map(button => (
              <CtaButton key={button._key} button={button} toLink={adapter.toLink} />
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const CtaButton: FC<{ button: CtaBlockButton; toLink: (raw: unknown) => { href: string; target?: string } | undefined }> = ({
  button,
  toLink,
}) => {
  const link = toLink(button.link);
  if (!link || !button.label) return null;

  return (
    <ButtonLink href={link.href} target={link.target} action={button.variant ?? 'primary'}>
      {button.label}
    </ButtonLink>
  );
};

export default CtaBlock;
